import { useMemo } from 'react'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { getConfigFromPowerForm } from './utils'

export const LeafRank = ({
  isElite,
  powerRank,
  powerForm
}: {
  isElite?: boolean
  powerRank: number | null
  powerForm: `${PowerPerformanceForms}` | null
}) => {
  const colorsConfig = useMemo(() => {
    return getConfigFromPowerForm(powerForm || PowerPerformanceForms.IN_FORM)
  }, [powerForm])

  if (!powerForm || powerForm === PowerPerformanceForms.NOT_RATED) return null

  return (
    <div className='flex items-center'>
      <div className='mr-[-6px]'>
        <LeftLeaf fill={colorsConfig.leafColor} />
      </div>
      <div style={{ color: colorsConfig.textColor }} className='text-sm'>
        {isElite && powerRank ? (
          <div className='mx-[2px] text-[13px] font-[500] mb-[3px] min-w-2 text-center'> {powerRank} </div>
        ) : (
          <LockIcon fill={colorsConfig.lockColor} />
        )}
      </div>

      <div className='ml-[-6px]'>
        <RightLeaf fill={colorsConfig.leafColor} />
      </div>
    </div>
  )
}

const LeftLeaf = ({ fill = '#ACE6BD' }: { fill?: string }) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M8.14032 1.48699C8.40415 1.06588 8.27666 0.510623 7.85555 0.24678C7.43444 -0.0170481 6.87919 0.110446 6.61534 0.531555L6.23044 1.14592C5.9666 1.56703 6.09409 2.12228 6.51522 2.38611C6.93633 2.64996 7.49158 2.52246 7.75541 2.10135L8.14032 1.48699ZM3.3473 2.54744C3.70809 2.20149 4.27789 2.20996 4.61997 2.56634L5.47994 3.46223L6.9257 3.06179C7.40624 2.9287 7.89923 3.20872 8.02682 3.68724C8.15443 4.16575 7.86833 4.66156 7.3878 4.79466L5.42548 5.33815C5.10706 5.42636 4.76788 5.33428 4.54118 5.09812L3.31347 3.81913C2.97138 3.46275 2.98653 2.8934 3.3473 2.54744ZM3.21489 5.61269C2.8389 5.29227 2.26959 5.33949 1.9433 5.71816C1.61702 6.09681 1.65731 6.66353 2.0333 6.98395L3.76817 8.46237C4.03288 8.68795 4.40675 8.73855 4.72674 8.5921L6.82388 7.6323C7.27837 7.42429 7.48216 6.89062 7.27905 6.4403C7.07593 5.98999 6.54281 5.79357 6.08831 6.00158L4.51577 6.72129L3.21489 5.61269ZM6.82516 10.7148C7.25512 10.4641 7.40142 9.91323 7.15191 9.48439C6.90241 9.05553 6.35159 8.91112 5.92161 9.16182L4.52441 9.97647L2.79636 9.27914C2.33592 9.09334 1.81073 9.31616 1.6233 9.77685C1.43588 10.2375 1.65719 10.7616 2.11763 10.9474L4.25478 11.8099C4.51282 11.914 4.80495 11.8927 5.04592 11.7522L6.82516 10.7148Z'
        fill={fill}
      />
      <path
        d='M9.07423 11.6064L8.45068 12.7998C9.09668 12.8512 10.7827 13.2358 12.3587 14.3626C12.7476 14.6718 12.8121 15.2379 12.5028 15.6268C12.1935 16.0157 11.6274 16.0802 11.2385 15.771C10.9783 15.489 9.98655 14.8327 8.10177 14.4629C7.79071 14.422 7.50293 14.452 7.22555 14.4629L7.1136 14.452C7.1009 14.453 7.08815 14.4537 7.07532 14.4541L4.11436 14.5656C3.61267 14.5844 3.18082 14.2019 3.14982 13.7111C3.11879 13.2202 3.50032 12.8071 4.00201 12.7882L6.44175 12.6964L7.44905 10.7685C7.67542 10.3353 8.22273 10.1716 8.67151 10.403C9.12029 10.6343 9.30059 11.1731 9.07423 11.6064Z'
        fill={fill}
      />
    </svg>
  )
}

const RightLeaf = ({ fill = '#ACE6BD' }: { fill?: string }) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M7.85968 1.48699C7.59585 1.06588 7.72334 0.510623 8.14445 0.24678C8.56556 -0.0170481 9.12081 0.110446 9.38466 0.531555L9.76956 1.14592C10.0334 1.56703 9.90591 2.12228 9.48478 2.38611C9.06367 2.64996 8.50842 2.52246 8.24459 2.10135L7.85968 1.48699ZM12.6527 2.54744C12.2919 2.20149 11.7221 2.20996 11.38 2.56634L10.5201 3.46223L9.0743 3.06179C8.59376 2.9287 8.10077 3.20872 7.97318 3.68724C7.84557 4.16575 8.13167 4.66156 8.6122 4.79466L10.5745 5.33815C10.8929 5.42636 11.2321 5.33428 11.4588 5.09812L12.6865 3.81913C13.0286 3.46275 13.0135 2.8934 12.6527 2.54744ZM12.7851 5.61269C13.1611 5.29227 13.7304 5.33949 14.0567 5.71816C14.383 6.09681 14.3427 6.66353 13.9667 6.98395L12.2318 8.46237C11.9671 8.68795 11.5933 8.73855 11.2733 8.5921L9.17612 7.6323C8.72163 7.42429 8.51784 6.89062 8.72095 6.4403C8.92407 5.98999 9.45719 5.79357 9.91169 6.00158L11.4842 6.72129L12.7851 5.61269ZM9.17484 10.7148C8.74488 10.4641 8.59858 9.91323 8.84809 9.48439C9.09759 9.05553 9.64841 8.91112 10.0784 9.16182L11.4756 9.97647L13.2036 9.27914C13.6641 9.09334 14.1893 9.31616 14.3767 9.77685C14.5641 10.2375 14.3428 10.7616 13.8824 10.9474L11.7452 11.8099C11.4872 11.914 11.195 11.8927 10.9541 11.7522L9.17484 10.7148Z'
        fill={fill}
      />
      <path
        d='M6.92577 11.6064L7.54932 12.7998C6.90332 12.8512 5.21732 13.2358 3.64128 14.3626C3.25243 14.6718 3.18793 15.2379 3.49717 15.6268C3.80655 16.0157 4.37261 16.0802 4.76149 15.771C5.02174 15.489 6.01345 14.8327 7.89823 14.4629C8.20929 14.422 8.49707 14.452 8.77445 14.4629L8.8864 14.452C8.8991 14.453 8.91185 14.4537 8.92468 14.4541L11.8856 14.5656C12.3873 14.5844 12.8192 14.2019 12.8502 13.7111C12.8812 13.2202 12.4997 12.8071 11.998 12.7882L9.55825 12.6964L8.55095 10.7685C8.32458 10.3353 7.77727 10.1716 7.32849 10.403C6.87971 10.6343 6.69941 11.1731 6.92577 11.6064Z'
        fill={fill}
      />
    </svg>
  )
}

const LockIcon = ({ fill = '#CC5C2D' }: { fill?: string }) => {
  return (
    <svg width='13' height='14' viewBox='0 0 13 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M6.42981 1.35714C5.68028 1.35714 5.07266 1.96476 5.07266 2.71429V3.39286H7.78695V2.71429C7.78695 1.96476 7.17934 1.35714 6.42981 1.35714ZM3.71552 2.71429V3.39286H3.37626C2.81411 3.39286 2.3584 3.84857 2.3584 4.41071V8.48214C2.3584 9.04427 2.81411 9.5 3.37626 9.5H9.4834C10.0455 9.5 10.5013 9.04427 10.5013 8.48214V4.41071C10.5013 3.84857 10.0455 3.39286 9.4834 3.39286H9.14411V2.71429C9.14411 1.21523 7.92887 0 6.42981 0C4.93075 0 3.71552 1.21523 3.71552 2.71429Z'
        fill={fill}
      />
    </svg>
  )
}
